/**
 * Runtime map configuration.
 *
 * The Razor view (PropertyListings.cshtml) may inject `window.TVR_MAP_CONFIG`
 * from appsettings so the Regrid token never has to be hardcoded or committed.
 * When nothing is injected we fall back to the free, public county parcel
 * services so the parcel-lines feature works with zero configuration.
 *
 * To switch to Regrid in production: set provider to "regrid" and supply a
 * token in appsettings (see PropertyListings.cshtml). No code change needed.
 */

export type ParcelProvider = "freeCounties" | "regrid";

export interface MapRuntimeConfig {
  parcelProvider: ParcelProvider;
  regridToken: string;
  /** True when an Umbraco member is signed in (drives the Save Search flow). */
  isSignedIn: boolean;
  /** Public URL of the My Account / member-area page; signed-out visitors are
   *  sent here to log in before saving a search or favoriting a listing. */
  accountUrl: string;
  /** MLS ListingIds the signed-in member has already favorited, injected by
   *  PropertyListings.cshtml so the hearts render in their saved state on load. */
  favorites: string[];
}

declare global {
  interface Window {
    TVR_MAP_CONFIG?: Partial<MapRuntimeConfig>;
  }
}

/** Regrid raster parcel tiles. Requires a paid, domain-restricted token. */
export const REGRID_TILE_URL = "https://tiles.regrid.com/api/v1/parcels/{z}/{x}/{y}.png";

const DEFAULTS: MapRuntimeConfig = {
  parcelProvider: "freeCounties",
  regridToken: "",
  isSignedIn: false,
  accountUrl: "",
  favorites: [],
};

function readConfig(): MapRuntimeConfig {
  const injected = (typeof window !== "undefined" && window.TVR_MAP_CONFIG) || {};

  // Accept the current values; treat the legacy "tetonCountyId" value (and
  // anything unknown) as the free multi-county provider.
  const provider: ParcelProvider = injected.parcelProvider === "regrid" ? "regrid" : "freeCounties";

  const regridToken =
    typeof injected.regridToken === "string" ? injected.regridToken.trim() : DEFAULTS.regridToken;

  const isSignedIn = injected.isSignedIn === true;
  const accountUrl =
    typeof injected.accountUrl === "string" ? injected.accountUrl.trim() : DEFAULTS.accountUrl;

  // Only trust the injected favorites for a signed-in member; drop anything
  // that isn't a non-empty string so a hand-edited config can't poison state.
  const favorites =
    isSignedIn && Array.isArray(injected.favorites)
      ? injected.favorites.filter((x): x is string => typeof x === "string" && x.length > 0)
      : DEFAULTS.favorites;

  // Regrid needs a token; without one, silently fall back to the free layers
  // so the toggle still does something useful.
  if (provider === "regrid" && !regridToken) {
    return { parcelProvider: "freeCounties", regridToken: "", isSignedIn, accountUrl, favorites };
  }

  return { parcelProvider: provider, regridToken, isSignedIn, accountUrl, favorites };
}

export const mapConfig: MapRuntimeConfig = readConfig();

/**
 * Where to send a signed-out visitor who tries a member action. The account
 * page shows the login form to guests, and after login the server honors a
 * `returnUrl` so they come back to the search they were on instead of landing
 * on the account page. Same shape favorites.js uses on the Razor pages.
 * Empty when no account URL was injected, so callers can no-op.
 */
export function loginRedirectUrl(): string {
  if (!mapConfig.accountUrl) return "";
  const sep = mapConfig.accountUrl.includes("?") ? "&" : "?";
  const here = window.location.pathname + window.location.search;
  return `${mapConfig.accountUrl}${sep}returnUrl=${encodeURIComponent(here)}`;
}
